import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, getProductsCatalog, createMockupTask, getProductDetails } from '../apiClient';
import FeedbackModal from './FeedbackModal';

const PrintOptions = ({ image, userFullName, onProductImageSelected, selectedProduct, position, size, rotation, printArea, onMockupGenerated, displayedProductImage, shippingAddress: initialShippingAddress, productDetails: initialProductDetails }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mockupToOrder } = location.state || {}; // Get mockup data from navigation state
  const [currentPrintImage, setCurrentPrintImage] = useState(image); // New state for the image to be printed/mocked
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [selectedModel, setSelectedModel] = useState(selectedProduct ? selectedProduct.id : '');
  const [detailedProductData, setDetailedProductData] = useState(initialProductDetails);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColorName, setSelectedColorName] = useState('');
  const [selectedFacility, setSelectedFacility] = useState(''); // No default
  const [quantity, setQuantity] = useState(1);
  const [shippingFirstName, setShippingFirstName] = useState(userFullName ? userFullName.split(' ')[0] : '');
  const [shippingLastName, setShippingLastName] = useState(userFullName ? userFullName.split(' ')[1] : '');
  const [shippingAddress1, setShippingAddress1] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingZip, setShippingZip] = useState('');
  const [shippingCountryCode, setShippingCountryCode] = useState('US');
  const [shippingEmail, setShippingEmail] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingState, setShippingState] = useState(''); // Initialize shippingState
  const [selectedPlacement, setSelectedPlacement] = useState(''); // New state for selected placement
  const [dynamicPlacementData, setDynamicPlacementData] = useState({}); // New state for dynamic placement inputs
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [mockupStatus, setMockupStatus] = useState('');
  const [mockupMessage, setMockupMessage] = useState('');
  const [mockupImageUrl, setMockupImageUrl] = useState('');

  useEffect(() => {
    if (detailedProductData && selectedPlacement) {
        const placementFile = detailedProductData.files.find(file => file.type === selectedPlacement);
        if (placementFile && placementFile.options) {
            const initialPlacementData = {};
            placementFile.options.forEach(option => {
                if (!option.id.startsWith('thread_colors_')) {
                    initialPlacementData[option.id] = option.default !== undefined ? option.default : '';
                }
            });
            setDynamicPlacementData(initialPlacementData);
        } else {
            setDynamicPlacementData({});
        }
    }
}, [detailedProductData, selectedPlacement]);

  useEffect(() => {
    if (initialShippingAddress) {
        setShippingFirstName(initialShippingAddress.first_name || '');
        setShippingLastName(initialShippingAddress.last_name || '');
        setShippingAddress1(initialShippingAddress.address1 || '');
        setShippingCity(initialShippingAddress.city || '');
        setShippingZip(initialShippingAddress.zip || '');
        setShippingCountryCode(initialShippingAddress.country_code || 'US');
        setShippingEmail(initialShippingAddress.email || '');
        setShippingPhone(initialShippingAddress.phone || '');
        setShippingState(initialShippingAddress.state || '');
    } else if (userFullName) {
        const nameParts = userFullName.split(' ');
        setShippingFirstName(nameParts[0] || '');
        setShippingLastName(nameParts.slice(1).join(' ') || '');
    }
  }, [initialShippingAddress, userFullName]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  const [displayPrice, setDisplayPrice] = useState(0);
  const [availability, setAvailability] = useState('');
  const [selectedStitchColor, setSelectedStitchColor] = useState('');
  const [generatedMockupId, setGeneratedMockupId] = useState(null); // New state for generated mockup ID

  // Debugging logs
  console.log("PrintOptions: productsCatalog", productsCatalog);
  console.log("PrintOptions: selectedModel", selectedModel);
  

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProductsCatalog();
        setProductsCatalog(data);
      } catch (error) {
        console.error('Error fetching products catalog:', error);
        setMessage('Failed to load product options.');
      }
    };
    fetchProducts(); // Always fetch the full catalog

    if (mockupToOrder) {
      // ... (existing logic for mockupToOrder)
    } else if (initialProductDetails) {
        setDetailedProductData(initialProductDetails);
        setSelectedModel(initialProductDetails.id);
        onProductImageSelected(initialProductDetails.image);
        setDisplayPrice(parseFloat(initialProductDetails.selling_price));
        let availabilityInfo = initialProductDetails.origin_country ? `Sourced from: ${initialProductDetails.origin_country}` : 'Availability not specified';
        if (initialProductDetails.description) {
            const availabilityMatch = initialProductDetails.description.match(/Important: (.*)/);
            if (availabilityMatch && availabilityMatch[1]) {
                availabilityInfo = availabilityMatch[1];
            }
        }
        setAvailability(availabilityInfo);
        const facilities = Object.keys(initialProductDetails.provider_mappings);
        const defaultFacility = facilities.length > 0 ? facilities[0] : '';
        setSelectedFacility(defaultFacility);
    } else if (image) {
      setCurrentPrintImage(image);
    }
  }, [initialProductDetails, mockupToOrder, image]);

  const handleModelChange = async (e) => {
    const modelId = parseInt(e.target.value, 10);
    setSelectedModel(modelId);
    setSelectedSize('');
    setSelectedColorName('');
    setSelectedPlacement('');

    if (modelId) {
      try {
        const details = await getProductDetails(modelId);
        setDetailedProductData(details);
        onProductImageSelected(details.image);
        setDisplayPrice(parseFloat(details.selling_price));

        let availabilityInfo = details.origin_country ? `Sourced from: ${details.origin_country}` : 'Availability not specified';
        if (details.description) {
          const availabilityMatch = details.description.match(/Important: (.*)/);
          if (availabilityMatch && availabilityMatch[1]) {
            availabilityInfo = availabilityMatch[1];
          }
        }
        setAvailability(availabilityInfo);
        // Set facility to the first available one for the new model
        const facilities = Object.keys(details.provider_mappings);
        const defaultFacility = facilities.length > 0 ? facilities[0] : '';
        setSelectedFacility(defaultFacility);
      } catch (error) {
        console.error('Error fetching detailed product data on model change:', error);
        setMessage('Failed to load detailed product information for selected model.');
        setDetailedProductData(null);
        onProductImageSelected('');
        setDisplayPrice(0);
        setSelectedFacility('');
      }
    } else {
      setDetailedProductData(null);
      onProductImageSelected('');
      setDisplayPrice(0);
      setSelectedFacility('');
    }
  };

  const handleSizeChange = (e) => {
    setSelectedSize(e.target.value);
  };

  const handleColorChange = (e) => {
    const newColorName = e.target.value;
    setSelectedColorName(newColorName);

    if (detailedProductData && newColorName) {
      const firstMatchingVariant = detailedProductData.variants.find(
        (variant) => variant.color === newColorName
      );

      if (firstMatchingVariant && firstMatchingVariant.image) {
        onProductImageSelected(firstMatchingVariant.image);
      } else {
        onProductImageSelected(detailedProductData.image);
      }
    }
  };

  const availableFacilities = useMemo(() => {
    if (!detailedProductData) return [];
    // Simplified: Assume Printful if variants exist
    return detailedProductData.variants ? ['Printful'] : [];
  }, [detailedProductData]);

  const availableSizes = useMemo(() => {
    if (!detailedProductData || !detailedProductData.variants) return [];
    const sizes = detailedProductData.variants.map(variant => variant.size);
    return [...new Set(sizes)];
  }, [detailedProductData]);

  const availableColors = useMemo(() => {
    if (!detailedProductData || !detailedProductData.variants) return [];
    const colors = detailedProductData.variants.map(variant => variant.color);
    return [...new Set(colors)].map(color => ({ name: color, hex: '' }));
  }, [detailedProductData]);

  const availablePlacements = useMemo(() => {
    if (!detailedProductData || !detailedProductData.files) return [];
    return detailedProductData.files
      .filter(file => file.type !== 'mockup' && file.type !== 'preview')
      .map(file => ({ type: file.type, title: file.title }));
  }, [detailedProductData]);

  const handleSendToPrint = async () => {
    if (!selectedModel || !selectedSize || !selectedColorName || !selectedFacility || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !selectedPlacement) {
      setMessage('Please fill in all required fields (T-shirt Model, Size, Color, Facility, Shipping Address including Address Line 1 and State, and Image Placement).');
      setLoading(false);
      return;
    }

    setLoading(true);
    setMessage('');

    const shippingAddress = {
      first_name: shippingFirstName,
      last_name: shippingLastName,
      address1: shippingAddress1,
      city: shippingCity,
      state: shippingState, // Add state
      zip: shippingZip,
      country_code: shippingCountryCode,
      email: shippingEmail,
      phone: shippingPhone,
    };

    try {
      const response = await api.post('/print-order', {
        image_url: currentPrintImage.image_url,
        prompt: currentPrintImage.prompt,
        style: currentPrintImage.style,
        tshirt_color: currentPrintImage.tshirt_color || '', // Ensure this is always a string
        printing_facility_name: selectedFacility,
        quantity: quantity,
        shipping_address: shippingAddress,
        tshirt_model: selectedModel,
        tshirt_size: selectedSize,
        tshirt_selected_color: selectedColorName,
        image_placement: selectedPlacement, // Add selected image placement
        stitch_color: selectedStitchColor, // Add selected stitch color
      });
      console.log("Sending to print with stitch_color:", selectedStitchColor);
      setMessage(`Order placed successfully with ${selectedFacility}! Order ID: ${response.data.order_id}`);
    } catch (error) {
      console.error('Error sending to print:', error);
      setMessage(`Failed to place order: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!selectedModel || !selectedSize || !selectedColorName || !selectedFacility || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !selectedPlacement) {
      setMessage('Please fill in all required fields (T-shirt Model, Size, Color, Facility, Shipping Address including Address Line 1 and State, and Image Placement) before proceeding to payment.');
      return;
    }

    const shippingAddress = {
      first_name: shippingFirstName,
      last_name: shippingLastName,
      address1: shippingAddress1,
      city: shippingCity,
      state: shippingState,
      zip: shippingZip,
      country_code: shippingCountryCode,
      email: shippingEmail,
      phone: shippingPhone,
    };

    const paymentDetails = {
      mockupId: generatedMockupId,
      quantity: quantity,
      totalAmount: parseFloat((displayPrice * quantity).toFixed(2)),
      currency: 'usd',
      shippingAddress: shippingAddress,
    };

    navigate('/payment', { state: paymentDetails });
  };

  const pollMockupStatus = async (mockupId) => {
    let attempts = 0;
    const maxAttempts = 20; // Poll for a maximum of 100 seconds

    const interval = setInterval(async () => {
      attempts++;
      try {
        const response = await api.get(`/mockups/${mockupId}/status`);
        const { status } = response.data;

        // Update status message based on polling attempts
        if (status.startsWith('polling_attempt_')) {
          const attemptNumber = status.split('_')[2];
          setMockupMessage(`Still working on it... (Attempt ${attemptNumber}/4)`);
        }

        if (status === 'completed') {
          setMockupStatus('completed');
          setMockupMessage('Mockup generated successfully! You can now proceed to payment.');
          clearInterval(interval);
          // Optionally, fetch the final mockup and update the UI
          const mockupDetails = await api.get(`/mockups/${mockupId}`);
          onMockupGenerated(mockupDetails.data);
          setGeneratedMockupId(mockupId); // Ensure generatedMockupId is set
          setDisplayPrice(parseFloat(mockupDetails.data.price)); // Update price from mockup
          setMockupImageUrl(mockupDetails.data.mockup_image_url); // Set the mockup image URL
        } else if (status === 'failed') {
          setMockupStatus('failed');
          setMockupMessage('Mockup generation failed. Please try again.');
          clearInterval(interval);
        } else if (status === 'scheduled_for_retry') {
            setMockupStatus('timeout');
            setMockupMessage("Hmmm this is taking more than expected, please return back later in your mockups page your will find the generated mockup");
            clearInterval(interval);
        }

        if (attempts >= maxAttempts && status !== 'completed') {
          setMockupStatus('timeout');
          setMockupMessage("Hmmm this is taking more than expected, please return back later in your mockups page your will find the generated mockup");
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Error polling mockup status:', error);
        setMockupStatus('failed');
        setMockupMessage('An error occurred while checking the mockup status.');
        clearInterval(interval);
      }
    }, 5000); // Poll every 5 seconds
  };

  const handleGenerateMockup = async () => {
    if (!selectedModel || !selectedSize || !selectedColorName || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !selectedPlacement || !shippingEmail || !shippingPhone) {
      setMessage('Please fill in all required fields (T-shirt Model, Size, Color, Shipping Address including Address Line 1, State, Email, and Phone, and Image Placement) to generate a mockup.');
      return;
    }

    setLoading(true);
    setIsFeedbackModalOpen(true);
    setMockupStatus('processing');
    setMockupMessage('Your mockup is being created... You will be notified when it is ready.');

    const mockupData = {
      image_id: currentPrintImage.id,
      image_url: currentPrintImage.image_url,
      tshirt_model: selectedModel,
      tshirt_size: selectedSize,
      tshirt_selected_color: selectedColorName,
      image_placement: selectedPlacement,
      stitch_color: selectedStitchColor,
      printing_facility_name: selectedFacility,
      shipping_address: {
        first_name: shippingFirstName,
        last_name: shippingLastName,
        address1: shippingAddress1,
        city: shippingCity,
        state: shippingState,
        zip: shippingZip,
        country_code: shippingCountryCode,
        email: shippingEmail,
        phone: shippingPhone
      },
      placement: {
        x: Math.round(((position.x / 100) * printArea.width) - (((size / 100) * printArea.width) / 2)),
        y: Math.round(((position.y / 100) * printArea.height) - (((size / 100) * printArea.height) / 2)),
        width: Math.round((size / 100) * printArea.width),
        height: Math.round((size / 100) * printArea.height),
        rotation: rotation,
      },
      ...(selectedPlacement && selectedPlacement.startsWith('embroidery_')
          ? { auto_thread_color: true }
          : {}
      ),
    };

    console.log("Data sent to createMockupTask:", mockupData);

    try {
      const response = await createMockupTask(mockupData);
      console.log("Create Mockup Response (full):", response);
      console.log("Create Mockup Response (data):", response.data); // Log response.data
      const { mockup_id } = response.data;
      pollMockupStatus(mockup_id);
    } catch (error) {
      console.error('Error generating mockup:', error);
      console.error('Error response:', error.response); // Log the error response
      setMockupStatus('failed');
      setMockupMessage(`Failed to start mockup generation: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-auto pt-4 border-t border-gray-200">
      {isFeedbackModalOpen && (
        <FeedbackModal
          status={mockupStatus}
          message={mockupMessage}
          onClose={() => setIsFeedbackModalOpen(false)}
          onProceedToPayment={handleProceedToPayment}
          mockupImageUrl={mockupImageUrl}
        />
      )}
      <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
        <p className="text-sm text-blue-700">
          This is just a mockup creation. Your address details are needed to calculate the shipping time.
        </p>
      </div>
      <h3 className="text-xl font-semibold mb-4 text-gray-800">Print Options</h3>

      {/* Product Preview */}
      {displayedProductImage && (
        <div className="mb-6 text-center">
          <img
            src={displayedProductImage}
            alt="Selected T-shirt"
            className="w-full max-w-xs mx-auto rounded-lg border border-gray-200 shadow-md"
          />
          {availability && <p className="text-sm text-gray-600 mt-3">{availability}</p>}
        </div>
      )}

      <div className="p-6 border border-gray-200 rounded-lg shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6">
        {/* Column 1: Product Selection */}
        <div>
          <div className="mb-4">
            <label htmlFor="tshirtModel" className="block text-sm font-medium text-gray-700 mb-1">T-shirt Model:</label>
            <select
              id="tshirtModel"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={selectedModel}
              onChange={handleModelChange}
            >
              <option value="">Select a model</option>
              {productsCatalog.map(model => (
                <option key={model.id} value={model.id}>{model.name}</option>
              ))}
            </select>
          </div>

          {detailedProductData && (
            <>
              <div className="mb-4">
                <label htmlFor="tshirtSize" className="block text-sm font-medium text-gray-700 mb-1">Size:</label>
                <select
                  id="tshirtSize"
                  className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  value={selectedSize}
                  onChange={handleSizeChange}
                >
                  <option value="">Select a size</option>
                  {availableSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="tshirtColor" className="block text-sm font-medium text-gray-700 mb-1">Color:</label>
                <select
                  id="tshirtColor"
                  className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  value={selectedColorName}
                  onChange={handleColorChange}
                >
                  <option value="">Select a color</option>
                  {availableColors.map(color => (
                    <option key={color.name} value={color.name}>{color.name}</option>
                  ))}
                </select>
              </div>

              {/* New Stitch Color Selection */}
              
                {detailedProductData && detailedProductData.options && detailedProductData.options.some(opt => opt.id === 'stitch_color') && (
                <div className="mb-4">
                  <label htmlFor="stitchColor" className="block text-sm font-medium text-gray-700 mb-1">Stitch Color:</label>
                  <select
                    id="stitchColor"
                    className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                    value={selectedStitchColor}
                    onChange={(e) => setSelectedStitchColor(e.target.value)}
                  >
                    <option value="">Select stitch color</option>
                    {detailedProductData.options.find(opt => opt.id === 'stitch_color').values &&
                      Object.entries(detailedProductData.options.find(opt => opt.id === 'stitch_color').values).map(([hex, name]) => (
                        <option key={hex} value={hex}>{name}</option>
                      ))}
                  </select>
                </div>
              )}

              {/* New Placement Selection */}
              {detailedProductData && (
                <div className="mb-4">
                  <label htmlFor="imagePlacement" className="block text-sm font-medium text-gray-700 mb-1">Image Placement:</label>
                  <select
                    id="imagePlacement"
                    className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                    value={selectedPlacement}
                    onChange={(e) => setSelectedPlacement(e.target.value)}
                    disabled={!selectedSize || !selectedColorName} // Disable until size and color are selected
                  >
                    <option value="">Select placement</option>
                    {availablePlacements.map(placement => (
                      <option key={placement.type} value={placement.type}>{placement.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Dynamic Placement Inputs / Thread Colors */}
              {selectedPlacement && selectedPlacement.startsWith('embroidery_') && (
                <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <h4 className="text-md font-semibold text-blue-800 mb-2">Embroidery Color Selection</h4>
                  <p className="text-sm text-blue-700">
                    You've selected an embroidery option. The thread color will be automatically chosen based on the colors in your image for the best result.
                  </p>
                </div>
              )}

              {/* Printing Facility - now a dropdown */}
              <div className="mb-4">
                <label htmlFor="facility" className="block text-sm font-medium text-gray-700 mb-1">Printing Facility:</label>
                <select
                  id="facility"
                  className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  value={selectedFacility}
                  onChange={(e) => setSelectedFacility(e.target.value)}
                >
                  <option value="">Select a facility</option>
                  {availableFacilities.map(facility => (
                    <option key={facility} value={facility}>{facility}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div className="mb-4">
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">Quantity:</label>
            <input
              type="number"
              id="quantity"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
            />
          </div>
        </div>

        {/* Column 2: Shipping Address */}
        <div>
          <div className="mb-4">
            <label htmlFor="shippingFirstName" className="block text-sm font-medium text-gray-700 mb-1">First Name:</label>
            <input
              type="text"
              id="shippingFirstName"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingFirstName}
              onChange={(e) => setShippingFirstName(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingLastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name:</label>
            <input
              type="text"
              id="shippingLastName"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingLastName}
              onChange={(e) => setShippingLastName(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingAddress1" className="block text-sm font-medium text-gray-700 mb-1">Address Line 1:</label>
            <input
              type="text"
              id="shippingAddress1"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingAddress1}
              onChange={(e) => setShippingAddress1(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingCity" className="block text-sm font-medium text-gray-700 mb-1">City:</label>
            <input
              type="text"
              id="shippingCity"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingCity}
              onChange={(e) => setShippingCity(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingZip" className="block text-sm font-medium text-gray-700 mb-1">Zip Code:</label>
            <input
              type="text"
              id="shippingZip"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingZip}
              onChange={(e) => setShippingZip(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingCountryCode" className="block text-sm font-medium text-gray-700 mb-1">Country Code:</label>
            <input
              type="text"
              id="shippingCountryCode"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingCountryCode}
              onChange={(e) => setShippingCountryCode(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingEmail" className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
            <input
              type="email"
              id="shippingEmail"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingEmail}
              onChange={(e) => setShippingEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone:</label>
            <input
              type="tel"
              id="shippingPhone"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingPhone}
              onChange={(e) => setShippingPhone(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shippingState" className="block text-sm font-medium text-gray-700 mb-1">State:</label>
            <input
              type="text"
              id="shippingState"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={shippingState}
              onChange={(e) => setShippingState(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      {displayPrice > 0 && (
        <p className="text-gray-800 font-bold mb-4 tour-estimated-cost">Estimated Cost: ${displayPrice.toFixed(2)}</p>
      )}

      {/* Conditional Buttons */}
      {!generatedMockupId ? (
        <button
          onClick={handleGenerateMockup}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-md transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Generating Mockup...' : 'Generate Mockup'}
        </button>
      ) : (
        displayPrice > 0 && (
          <button
            onClick={handleProceedToPayment}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !selectedModel || !selectedSize || !selectedColorName || !selectedFacility || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !selectedPlacement}
          >
            Proceed to Payment (${(displayPrice * quantity).toFixed(2)})
          </button>
        )
      )}

      </div>

      {message && <p className="mt-4 text-sm text-center text-gray-600">{message}</p>}

    </div>
  );
};

export default PrintOptions;